import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { ReportBlock } from "@/profitability/types";

/** Bar heights, as a share of the plot, so the shape reads as a chart. */
const BARS = [0.9, 0.74, 0.55, 0.47, 0.33, 0.22, 0.14, 0.09];
const ROWS = 6;

/**
 * A block's shape while its rows load.
 *
 * A dashboard asks for one query per block, so a page of spinners is what a
 * reader used to get. A skeleton the size of the figure, chart or table that
 * is coming says the same thing without the page flickering, and holds the
 * layout still so nothing jumps when the numbers land.
 */
export const BlockSkeleton = ({
  type,
  compact = false,
}: {
  type: ReportBlock["type"];
  /** A supporting figure, which has no card of its own. */
  compact?: boolean;
}) => {
  const pad = compact ? undefined : "px-4 pb-4";

  if (type === "summary")
    return (
      <Stack align="start" gap={0} className={pad} aria-hidden>
        <Skeleton className={compact ? "h-7 w-24" : "h-9 w-48"} />
      </Stack>
    );

  if (type === "table")
    return (
      <Stack gap={0} align="stretch" className={cn("gap-2", pad)} aria-hidden>
        <Skeleton className="h-3 w-full" />
        {Array.from({ length: ROWS }, (_, row) => (
          <Stack
            className="w-full"
            key={row}
            direction="row"
            align="center"
            gap={4}
          >
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-20 flex-none" />
            <Skeleton className="h-4 w-20 flex-none" />
          </Stack>
        ))}
      </Stack>
    );

  return (
    <Stack
      direction="row"
      align="end"
      justify="between"
      gap={2}
      className={cn("h-48", pad)}
      aria-hidden
    >
      {BARS.map((share, bar) => (
        <Skeleton
          key={bar}
          className="w-full rounded-sm"
          style={{ height: `${share * 100}%` }}
        />
      ))}
    </Stack>
  );
};
